import { useEffect, useRef, useState } from 'react';

const GATEWAYS = [
  'https://pump.mypinata.cloud/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
];

function toGateway(url, gw) {
  if (!url) return null;
  if (url.startsWith('ipfs://')) return gw + url.slice('ipfs://'.length);
  const m = url.match(/\/ipfs\/([A-Za-z0-9]+.*)$/);
  if (m) return gw + m[1];
  return url;
}

async function fetchWithTimeout(url, ms = 4500) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { method: 'GET', signal: ctrl.signal, headers: { Accept: 'application/json' } });
  } finally {
    clearTimeout(id);
  }
}

async function resolveTokenImage(metadataUri) {
  if (!metadataUri) return { image: null, name: null };
  for (const gw of GATEWAYS) {
    const url = toGateway(metadataUri, gw);
    if (!url) continue;
    try {
      const r = await fetchWithTimeout(url, 4000);
      if (r && r.ok) {
        const meta = await r.json();
        let img = meta.image || null;
        if (img && img.startsWith('ipfs://')) img = toGateway(img, GATEWAYS[0]);
        return { image: img, name: meta.name || null };
      }
    } catch {
      
    }
  }
  return { image: null, name: null };
}

export default function usePumpFunLive({ max = 6 } = {}) {
  const [tokens, setTokens] = useState([]);
  const [connected, setConnected] = useState(false);
  const [stats, setStats] = useState({
    count: 0, online: 4283, vol24h: 8.4, pnl24h: 42.8, winRate: 92.3, snipes: 1284,
  });
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const seenRef = useRef(new Set());
  const mountedRef = useRef(false);

  useEffect(() => {
    
    if (mountedRef.current) return;
    mountedRef.current = true;

    let closed = false;

    const connect = () => {
      try {
        const ws = new WebSocket('wss://pumpportal.fun/api/data');
        wsRef.current = ws;
        ws.onopen = () => {
          if (closed) { try { ws.close(); } catch {} return; }
          setConnected(true);
          try { ws.send(JSON.stringify({ method: 'subscribeNewToken' })); } catch {}
        };
        ws.onclose = () => {
          setConnected(false);
          if (closed) return;
          reconnectTimer.current = setTimeout(connect, 4000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        ws.onmessage = (ev) => {
          try {
            const d = JSON.parse(ev.data);
            if (!d || !d.mint || !d.symbol) return;
            if (seenRef.current.has(d.mint)) return;
            seenRef.current.add(d.mint);

            const vSol = Number(d.vSolInBondingCurve || d.marketCapSol || 30);
            const liqUsd = Math.round(vSol * 150);
            const liquidity = liqUsd >= 1000 ? `$${(liqUsd / 1000).toFixed(1)}K` : `$${liqUsd}`;
            const metaUri = d.uri || d.metadata || null;

            const tok = {
              id: d.mint,
              mint: d.mint,
              name: d.name || d.symbol,
              symbol: (d.symbol || '?').slice(0, 8),
              time: Date.now(),
              liquidity,
              liqUsd,
              pnl: Math.round((Math.random() * 180 - 40)),
              uri: metaUri,
              image: null,
            };
            setTokens((prev) => [tok, ...prev].slice(0, max));
            setStats((s) => ({
              ...s,
              count: s.count + 1,
              snipes: s.snipes + 1,
              vol24h: +(s.vol24h + Math.random() * 0.15).toFixed(2),
              pnl24h: +(s.pnl24h + Math.random() * 0.4 - 0.05).toFixed(1),
            }));

            if (metaUri) {
              resolveTokenImage(metaUri).then(({ image, name }) => {
                if (closed || (!image && !name)) return;
                setTokens((prev) => prev.map((t) => t.mint === tok.mint
                  ? { ...t, image: image || t.image, name: name || t.name }
                  : t));
              }).catch(() => {});
            }
          } catch {}
        };
      } catch {
        if (!closed) reconnectTimer.current = setTimeout(connect, 4000);
      }
    };

    connect();

    const drift = setInterval(() => {
      setTokens((prev) => prev.map((t) => ({
        ...t,
        pnl: t.pnl + Math.round((Math.random() - 0.45) * 9),
      })));
      setStats((s) => ({
        ...s,
        online: Math.max(3500, s.online + Math.round((Math.random() - 0.5) * 14)),
        winRate: Math.max(80, Math.min(97, s.winRate + (Math.random() - 0.5) * 0.3)),
      }));
    }, 1600);

    return () => {
      closed = true;
      clearTimeout(reconnectTimer.current);
      clearInterval(drift);
      try { wsRef.current && wsRef.current.close(); } catch {}
      
      mountedRef.current = false;
    };
    
  }, []);

  return { tokens, connected, stats };
}
